import React, { useState, useEffect } from 'react'
import classnames from 'classnames'

import ProductOptionsFilterList from './productOptionsFilterList'
import { connect } from 'react-redux'
import { updateSelectedOption, updateSurcharge } from '../coverageActions'
import { selectCoverageProducts } from '../../../reducers/refis'
import { selectBusinessConfig } from '../../../reducers'
import CoverageSurchargeFilterList from './CoverageSurchargesFilterList'

export const ProductOptionsFilter = ({
  data,
  selectedCoverageProducts,
  onChangeFilters,
  onChangeSurcharges,
  updateSurcharge,
  onApplyDiscount,
  savedFilters,
  savedAddons,
  savedDiscount,
  businessConfig,
  discountDisabled,
}) => {
  const [uiState, setUiState] = useState(true)
  const [discountAmount, setDiscountAmount] = useState('')
  const [discountType, setDiscountType] = useState<'%' | '#'>('%')
  const [discountUiState, setDiscountUiState] = useState(false)
  const [selectedDeductible, setSelectedDeductible] = useState<number[]>([])
  const [selectedMonths, setSelectedMonths] = useState<number[]>([])
  const [selectedMiles, setSelectedMiles] = useState<number[]>([])
  useEffect(() => {
    if (savedFilters) {
      const { deductibles, coverage_months, coverage_miles } = savedFilters
      if (Array.isArray(deductibles)) {
        setSelectedDeductible(deductibles)
      }
      if (Array.isArray(coverage_months)) {
        setSelectedMonths(coverage_months)
      }
      if (Array.isArray(coverage_miles)) {
        setSelectedMiles(coverage_miles)
      }
    }
  }, [savedFilters])

  // seed saved discount into UI and auto-open its panel
  useEffect(() => {
    if (savedDiscount) {
      setDiscountAmount(
        savedDiscount.amount != null ? String(savedDiscount.amount) : ''
      )
      setDiscountType(savedDiscount.type === '#' ? '#' : '%') // default to '%'
      setDiscountUiState(true)
    }
  }, [savedDiscount])

  // auto-apply discount any time amount or type changes
  useEffect(() => {
    const correction = onApplyDiscount({ amount: discountAmount, discountType })

    // If onApplyDiscount returned a correction, update the input field
    if (correction && correction.correctedAmount !== undefined) {
      setDiscountAmount(correction.correctedAmount.toString())
    }
  }, [discountAmount, discountType])

  const toggleUiState = () => setUiState(!uiState)

  const buttonClasses = classnames('refi-app__section-header__button', {
    'refi-app__section-header__button--open': uiState,
  })

  const sectionClass = classnames('', {
    'refi-app__section--open': uiState,
  })
  const discountButtonClasses = classnames('refi-app__section-header__button', {
    'refi-app__section-header__button--open': discountUiState,
  })
  const discountSectionClass = classnames('', {
    'refi-app__section--open': discountUiState,
  })

  const normalizeValue = (value) => {
    return value === 'Any' || value === 'None' ? null : value
  }

  const changeSelectedDeductible = (value) => {
    const val = normalizeValue(value)
    const next = selectedDeductible.includes(val)
      ? selectedDeductible.filter((x) => x !== val)
      : [...selectedDeductible, val]
    setSelectedDeductible(next)
    onChangeFilters({ deductibles: next })
  }
  const changeSelectedMonths = (value) => {
    const val = normalizeValue(value)
    const next = selectedMonths.includes(val)
      ? selectedMonths.filter((x) => x !== val)
      : [...selectedMonths, val]
    setSelectedMonths(next)
    onChangeFilters({ coverage_months: next })
  }
  const changeSelectedMiles = (value) => {
    const val = normalizeValue(value)
    const next = selectedMiles.includes(val)
      ? selectedMiles.filter((x) => x !== val)
      : [...selectedMiles, val]
    setSelectedMiles(next)
    onChangeFilters({ coverage_miles: next })
  }

  const onChangeSurcharge = (value) => {
    const newVal = value === 'None' ? 'Surcharge None' : value['SurchargeCode']
    updateSurcharge(newVal)
    onChangeSurcharges(value)
  }

  const addonsText =
    'Does your vehicle has any of the following special equipment / features / usage?'

  return (
    <div className={sectionClass}>
      <div
        className="refi-app__section-header"
        onClick={() => toggleUiState()}
        data-testid="coverage-accordian-toggle"
      >
        <p>Choose what you would like to compare</p>
        <button className={buttonClasses}>Toggle</button>
        <hr className="form__hr" />
      </div>

      <div className="coverage__padding-left">
        {uiState && (
          <>
            <ProductOptionsFilterList
              initialOpen={
                Array.isArray(savedFilters?.deductibles) &&
                savedFilters.deductibles.length > 0
              }
              value={selectedDeductible}
              data={data.deductibles}
              changeSelected={changeSelectedDeductible}
              setName={() => 'Deductible'}
              subsectionId={'deductible-filter-list'}
            />
            <ProductOptionsFilterList
              initialOpen={
                Array.isArray(savedFilters?.coverage_months) &&
                savedFilters.coverage_months.length > 0
              }
              value={selectedMonths}
              data={data.coverage_months}
              changeSelected={changeSelectedMonths}
              setName={() => 'Coverage period'}
              subsectionId={'months-filter-list'}
            />
            <ProductOptionsFilterList
              initialOpen={
                Array.isArray(savedFilters?.coverage_miles) &&
                savedFilters.coverage_miles.length > 0
              }
              value={selectedMiles}
              data={data.coverage_miles}
              changeSelected={changeSelectedMiles}
              setName={() => 'Mileage'}
              subsectionId={'miles-filter-list'}
            />
            <CoverageSurchargeFilterList
              initialOpen={Array.isArray(savedAddons) && savedAddons.length > 0}
              value={selectedCoverageProducts.selectedSurcharges || savedAddons}
              data={data.surchargesData}
              changeSelected={onChangeSurcharge}
              setName={() => `Add-ons - ${addonsText}`}
              subsectionId={'addons-filter-list'}
              requiredAddons={businessConfig.packages?.required_addons ?? []}
            />

            {!discountDisabled && (
              <div className={discountSectionClass}>
                <div
                  className="refi-app__section-header"
                  onClick={() => setDiscountUiState(!discountUiState)}
                  data-testid="coverage-sub-list-accordian"
                >
                  <p className="coverage__sub-header coverage__sub-header--blue">
                    Discount
                  </p>
                  <button className={discountButtonClasses}>Toggle</button>
                  <hr className="form__hr" />
                </div>

                {discountUiState && (
                  <div className="refi-payment-options__section">
                    <div className="form__group form__group--inline">
                      <input
                        type="text"
                        className="form__input form__input--inline"
                        placeholder="Amount % or #"
                        value={discountAmount}
                        onChange={(e) => {
                          const {
                            profit_discount_percent,
                            profit_discount_max,
                          } = businessConfig.profit

                          let val = e.target.value

                          // Allow empty string for clearing
                          if (val === '') {
                            setDiscountAmount('')
                            return
                          }

                          // Only validate if there's actually a numeric value
                          const numVal = parseFloat(val) // Changed from parseInt to parseFloat

                          if (!isNaN(numVal)) {
                            if (
                              discountType === '%' &&
                              numVal > profit_discount_percent
                            ) {
                              val = profit_discount_percent.toString()
                            }
                            if (
                              discountType === '#' &&
                              numVal > profit_discount_max
                            ) {
                              val = profit_discount_max.toString()
                            }
                          }

                          setDiscountAmount(val)
                        }}
                      />
                      <div
                        style={{
                          display: 'inline-block',
                          width: '100px',
                          margin: '0 1rem',
                        }}
                      >
                        <div className="select-wrapper">
                          <select
                            id="discount-type"
                            className="form__control form__control--select"
                            value={discountType ?? '%'}
                            onChange={(e) => {
                              const {
                                profit_discount_percent,
                                profit_discount_max,
                              } = businessConfig.profit

                              const newType = e.target.value as '%' | '#'
                              const currentValue = parseFloat(discountAmount)

                              setDiscountType(newType)

                              // Handle value validation when switching types
                              if (
                                !isNaN(currentValue) &&
                                discountAmount !== ''
                              ) {
                                let adjustedValue = discountAmount

                                if (newType === '%') {
                                  // Switching TO percentage: ensure value doesn't exceed max percentage
                                  if (currentValue > profit_discount_percent) {
                                    adjustedValue =
                                      profit_discount_percent.toString()
                                  }
                                } else if (newType === '#') {
                                  // Switching TO dollar amount: ensure value doesn't exceed max dollar amount
                                  if (currentValue > profit_discount_max) {
                                    adjustedValue =
                                      profit_discount_max.toString()
                                  }
                                }

                                // Update the amount if it was adjusted
                                if (adjustedValue !== discountAmount) {
                                  setDiscountAmount(adjustedValue)
                                }
                              }
                            }}
                            aria-label="Discount type"
                          >
                            <option value="%">%</option>
                            <option value="#">#</option>
                          </select>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="button button-drive-america"
                        style={{ display: 'none' }}
                        onClick={() =>
                          onApplyDiscount({
                            amount: discountAmount,
                            type: discountType,
                          })
                        }
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export const mapStateToProps = (state) => ({
  selectedCoverageProducts: selectCoverageProducts(state),
  businessConfig: selectBusinessConfig(state),
})

export default connect(mapStateToProps, {
  updateSelectedOption,
  updateSurcharge,
})(ProductOptionsFilter)
